'use client';

import React, { useState } from 'react';
import {
  Car,
  Calendar,
  Briefcase,
  MapPin,
  Clock,
  Search,
} from 'lucide-react';
import { RENTAL_LOCATIONS } from '@/lib/data';
import { useLocale } from '@/lib/i18n';

interface SearchBookingBarProps {
  onSearch: (criteria: {
    location: string;
    pickupDate: string;
    pickupTime: string;
    dropoffDate: string;
    dropoffTime: string;
    rentalType: string;
  }) => void;
}

export function SearchBookingBar({ onSearch }: SearchBookingBarProps) {
  const { t } = useLocale();
  const [rentalType, setRentalType] = useState<'daily' | 'longterm' | 'corporate'>('daily');
  const [location, setLocation] = useState('Miami International Airport (MIA)');
  const [pickupDate, setPickupDate] = useState('2026-10-18');
  const [pickupTime, setPickupTime] = useState('10:00 AM');
  const [dropoffDate, setDropoffDate] = useState('2026-10-22');
  const [dropoffTime, setDropoffTime] = useState('10:00 AM');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      location,
      pickupDate,
      pickupTime,
      dropoffDate,
      dropoffTime,
      rentalType,
    });
  };

  const times = [
    '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM',
    '04:00 PM', '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM'
  ];

  return (
    <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-8 -mt-16 sm:-mt-20">
      <div className="bg-[#181d24] rounded-t-2xl px-4 py-2.5 sm:px-6 inline-flex items-center gap-2 sm:gap-6 border-t border-x border-slate-700/60 shadow-lg">
        {/* Tab 1: Rent a Car */}
        <button
          type="button"
          onClick={() => setRentalType('daily')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
            rentalType === 'daily'
              ? 'bg-[var(--color-primary)] text-white shadow-sm'
              : 'text-slate-300 hover:text-white hover:bg-slate-800'
          }`}
        >
          <Car className="w-4 h-4" />
          <span>{t('search.rent')}</span>
        </button>

        {/* Tab 2: Long Term Rental */}
        <button
          type="button"
          onClick={() => setRentalType('longterm')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
            rentalType === 'longterm'
              ? 'bg-[var(--color-primary)] text-white shadow-sm'
              : 'text-slate-300 hover:text-white hover:bg-slate-800'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>{t('search.longterm')}</span>
        </button>

        {/* Tab 3: Corporate Rental */}
        <button
          type="button"
          onClick={() => setRentalType('corporate')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
            rentalType === 'corporate'
              ? 'bg-[var(--color-primary)] text-white shadow-sm'
              : 'text-slate-300 hover:text-white hover:bg-slate-800'
          }`}
        >
          <Briefcase className="w-4 h-4" />
          <span>{t('search.corporate')}</span>
        </button>
      </div>

      {/* Main Search Panel Card (White rounded card with high-precision inputs) */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-b-2xl rounded-tr-2xl p-4 sm:p-5 lg:p-6 shadow-xl border border-slate-200/80"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4 items-center">
          {/* Pick-up Location (Span 3 on desktop) */}
          <div className="lg:col-span-3 bg-slate-50 hover:bg-slate-100/80 transition-colors p-3 rounded-xl border border-slate-200/90 flex flex-col justify-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
              {t('search.pickupLoc')}
            </span>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
              >
                {RENTAL_LOCATIONS.map((loc) => (
                  <option key={loc.id} value={`${loc.name} (${loc.code})`}>
                    {loc.name} ({loc.code})
                  </option>
                ))}
                <option value="Downtown Miami Branch">Downtown Miami Branch</option>
                <option value="Orlando Resort Center">Orlando Resort Center</option>
                <option value="Los Angeles Downtown">Los Angeles Downtown</option>
              </select>
            </div>
          </div>

          {/* Pick-up Date (Span 2) */}
          <div className="lg:col-span-2 bg-slate-50 hover:bg-slate-100/80 transition-colors p-3 rounded-xl border border-slate-200/90 flex flex-col justify-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
              {t('search.pickupDate')}
            </span>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
              />
            </div>
          </div>

          {/* Pick-up Time (Span 2) */}
          <div className="lg:col-span-2 bg-slate-50 hover:bg-slate-100/80 transition-colors p-3 rounded-xl border border-slate-200/90 flex flex-col justify-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
              {t('search.pickupTime')}
            </span>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
              <select
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
              >
                {times.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Drop-off Date (Span 2) */}
          <div className="lg:col-span-2 bg-slate-50 hover:bg-slate-100/80 transition-colors p-3 rounded-xl border border-slate-200/90 flex flex-col justify-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
              {t('search.dropDate')}
            </span>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
              <input
                type="date"
                value={dropoffDate}
                onChange={(e) => setDropoffDate(e.target.value)}
                className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
              />
            </div>
          </div>

          {/* Drop-off Time (Span 1 or 1.5) */}
          <div className="lg:col-span-1 bg-slate-50 hover:bg-slate-100/80 transition-colors p-3 rounded-xl border border-slate-200/90 flex flex-col justify-center">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1 truncate">
              {t('search.dropTime')}
            </span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
              <select
                value={dropoffTime}
                onChange={(e) => setDropoffTime(e.target.value)}
                className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
              >
                {times.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* SEARCH CARS Button (Span 2) */}
          <div className="sm:col-span-2 lg:col-span-2 flex items-center h-full">
            <button
              type="submit"
              className="w-full h-[58px] bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-[0.98] text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <Search className="w-4 h-4" />
              <span>{t('search.cta')}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
